import React from 'react';
import { AlertTriangle, Clock, CheckCircle, History, ShieldAlert } from 'lucide-react';
import { EMERGENCY_STATUS } from './vaultConstants';
import { formatDate, getTimeRemaining } from './vaultUtils';

export function EmergencyAccessTab({
  isOwner,
  emergencyRequests,
  myEmergencyRequest,
  processingRequestId,
  onApprove,
  onDeny,
  onRevoke,
  onRequestClick,
}) {
  const pending = emergencyRequests.filter(r => r.status === 'pending');
  const active = emergencyRequests.filter(r => ['approved', 'auto_granted'].includes(r.status));
  const history = emergencyRequests.filter(r => ['denied', 'revoked'].includes(r.status));

  return (
    <div className="space-y-4" data-testid="emergency-access-content">
      {isOwner ? (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800">Emergency Access Requests</p>
              <p className="text-red-700">
                Executors can request emergency access to all vault documents. Requests auto-grant after 72 hours if not responded to.
                You'll receive reminder emails at 24h and 48h.
              </p>
            </div>
          </div>

          {pending.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                Pending Requests
              </h3>
              {pending.map(req => (
                <div key={req.request_id} className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid={`emergency-request-${req.request_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900">{req.requester_name}</span>
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                          {getTimeRemaining(req.auto_grant_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Reason:</strong> {req.reason}
                      </p>
                      <p className="text-xs text-gray-500">
                        Requested {formatDate(req.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onApprove(req.request_id)}
                        disabled={processingRequestId === req.request_id}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        data-testid={`approve-${req.request_id}`}
                      >
                        {processingRequestId === req.request_id ? '...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => onDeny(req.request_id)}
                        disabled={processingRequestId === req.request_id}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        data-testid={`deny-${req.request_id}`}
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {active.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Active Emergency Access
              </h3>
              {active.map(req => {
                const statusInfo = EMERGENCY_STATUS[req.status];
                return (
                  <div key={req.request_id} className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid={`active-access-${req.request_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-900">{req.requester_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          Granted {formatDate(req.resolved_at || req.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => onRevoke(req.request_id)}
                        disabled={processingRequestId === req.request_id}
                        className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                        data-testid={`revoke-${req.request_id}`}
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {history.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-4 h-4 text-gray-600" />
                Request History
              </h3>
              <div className="space-y-2">
                {history.map(req => {
                  const statusInfo = EMERGENCY_STATUS[req.status];
                  return (
                    <div key={req.request_id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-700">{req.requester_name}</span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(req.resolved_at || req.created_at)}
                          {req.resolution_note && ` - ${req.resolution_note}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {emergencyRequests.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <AlertTriangle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No emergency access requests</p>
              <p className="text-sm text-gray-500">Executors can request emergency access if needed</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800">Emergency Access</p>
              <p className="text-red-700">
                As a designated executor, you can request emergency access to all vault documents.
                The vault owner will be notified and has 72 hours to approve or deny your request.
              </p>
            </div>
          </div>

          {myEmergencyRequest ? (
            <div className={`rounded-lg border p-6 ${
              myEmergencyRequest.status === 'pending' ? 'bg-amber-50 border-amber-200' :
              ['approved', 'auto_granted'].includes(myEmergencyRequest.status) ? 'bg-green-50 border-green-200' :
              'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const statusInfo = EMERGENCY_STATUS[myEmergencyRequest.status];
                  const StatusIcon = statusInfo.icon;
                  return (
                    <>
                      <StatusIcon className={`w-8 h-8 ${statusInfo.color.split(' ')[1]}`} />
                      <div>
                        <h3 className="font-semibold text-gray-900">Your Emergency Access Request</h3>
                        <span className={`text-sm px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="space-y-2 text-sm">
                <p><strong>Reason:</strong> {myEmergencyRequest.reason}</p>
                <p><strong>Submitted:</strong> {formatDate(myEmergencyRequest.created_at)}</p>
                {myEmergencyRequest.status === 'pending' && (
                  <p className="text-amber-700 font-medium">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {getTimeRemaining(myEmergencyRequest.auto_grant_at)}
                  </p>
                )}
                {myEmergencyRequest.resolved_at && (
                  <p><strong>Resolved:</strong> {formatDate(myEmergencyRequest.resolved_at)}</p>
                )}
                {myEmergencyRequest.resolution_note && (
                  <p><strong>Note:</strong> {myEmergencyRequest.resolution_note}</p>
                )}
              </div>

              {['approved', 'auto_granted'].includes(myEmergencyRequest.status) && (
                <div className="mt-4 p-3 bg-green-100 rounded-lg text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  You have emergency access to all executor-level vault documents.
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <ShieldAlert className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Request Emergency Access</h3>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                If you need immediate access to vault documents for estate management purposes,
                you can submit an emergency access request.
              </p>
              <button
                onClick={onRequestClick}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                data-testid="request-emergency-access-btn"
              >
                <AlertTriangle className="w-4 h-4" />
                Request Emergency Access
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
